import {Page} from './page/page';
import {User} from './user/user';
import {Product} from './product/product';
import {OmapiIdConstants} from '../../shared/omapi-id-constants';
import {Cart} from './cart/cart';
import {Event} from './event/event';
import {AnalyticsOrder} from './analytics-order';
import {Price} from './price/price';
import {Item} from './item/item';
import {ProductInfo} from './product/productInfo';
import {Transaction} from './transaction/transaction';
import {Attribute} from './event/attribute';

export class DigitalData {
  appId: string;
  brand: string;
  page: Page;
  user: User;
  product: Product[];
  cart: Cart;
  event: Event[];
  transaction: Transaction;

  private constructor(pageName: string, language: string, user: User, product: Product[], cart: Cart, events: Event[], transaction: Transaction) {
    this.appId = 'EOS TV Shop';
    this.brand = 'telenet';
    this.page = new Page(pageName, language);
    this.user = user;
    this.product = product;
    this.cart = cart;
    this.event = events;
    this.transaction = transaction;
  }

  public static createForHomePage(language: string, user: string, omapiIds: Array<string>): DigitalData {
    return new DigitalData('tvshop/homepage', language, new User(user), this.mapToProducts(omapiIds), undefined, [], undefined);
  }

  public static createForDetailPage(language: string, user: string, omapiIds: Array<string>) {
    return new DigitalData(this.createDetailPageName(omapiIds[0]), language, new User(user),
      this.mapToProducts(omapiIds),
      undefined,
      [],
      undefined);
  }

  public static createForOptionsPage(language: string, user: string, omapiIds: Array<string>) {
    return new DigitalData(this.createDetailPageName(omapiIds[0]) + '/option', language, new User(user),
      this.mapToProducts(omapiIds),
      undefined,
      [],
      undefined);
  }

  public static createForOrderModal(currentLanguage: string, user: string, order: AnalyticsOrder): DigitalData {
    return this.createDigitalDate(order, currentLanguage, user, new Event('add to cart', 'add', 'button', undefined));
  }

  public static createForPinEntered(currentLanguage: string, user: string, order: AnalyticsOrder): DigitalData {
    return this.createDigitalDate(order, currentLanguage, user, new Event('order confirmation', 'pincode', 'form', undefined));
  }

  public static createForOrderSuccess(currentLanguage: string, user: string, order: AnalyticsOrder): DigitalData {
    return new DigitalData(
      this.createDetailPageName(order.packOmapiId) + '/thankyou', currentLanguage,
      new User(user),
      undefined,
      undefined,
      [],
      DigitalData.mapToTransaction(order));
  }

  public static createForVoucherEntered(currentLanguage: string, user: string): DigitalData {
    return new DigitalData(
      'tvshop/voucher',
      currentLanguage,
      new User(user),
      undefined,
      undefined,
      [new Event('Voucher', 'voucher entry', 'form', undefined)],
      undefined);
  }

  public static createForVoucherResolved(currentLanguage: string, user: string, trialOmapiIds: string[]): DigitalData {
    return new DigitalData(
      'tvshop/voucher',
      currentLanguage,
      new User(user),
      undefined,
      undefined,
      this.createEventForTrials(trialOmapiIds, 'voucher entry'),
      undefined);
  }

  public static createVoucherCodeFailed(currentLanguage: string, user: string): DigitalData {
    return new DigitalData(
      'tvshop/voucher',
      currentLanguage,
      new User(user),
      undefined,
      undefined,
      [new Event('Voucher', 'voucher fail', 'form', undefined)],
      undefined);
  }

  public static createVoucherCodeSuccess(currentLanguage: string, user: string, trialOmapiId: string): DigitalData {
    return new DigitalData(
      'tvshop/voucher',
      currentLanguage,
      new User(user),
      undefined,
      undefined,
      this.createEventForTrials([trialOmapiId], 'voucher succes'),
      undefined);
  }

  private static createEventForTrials(trialOmapiIds: string[], eventAction: string): Event[] {
    return trialOmapiIds.map(value => new Event('Voucher', eventAction, 'form', new Attribute(new ProductInfo(value, OmapiIdConstants.PRODUCT_NAME_MAP.get(value)))));
  }

  private static createDigitalDate(order: AnalyticsOrder, currentLanguage: string, user: string, event) {
    return new DigitalData(
      this.createDetailPageName(order.packOmapiId),
      currentLanguage,
      new User(user),
      undefined,
      this.mapToCart(order),
      [event],
      undefined
    );
  }

  private static mapToProducts(omapiIds: Array<string>) {
    const products = [];
    omapiIds.forEach((omapiId, index) => products[index] = new Product(omapiId, OmapiIdConstants.PRODUCT_NAME_MAP.get(omapiId)));
    return products;
  }

  private static createDetailPageName(omapiId: string): string {
    return 'tvshop/' + OmapiIdConstants.PRODUCT_NAME_MAP.get(omapiId).replace(/ /g, '').toLocaleLowerCase();
  }

  private static mapToCart(order: AnalyticsOrder): Cart {
    return new Cart(new Price(order.totalPrice()), this.createCartItems(order));
  }

  private static createCartItems(order: AnalyticsOrder) {
    if (order.subPackOmapiId) {
      return [
        new Item(
          new ProductInfo(order.packOmapiId, OmapiIdConstants.PRODUCT_NAME_MAP.get(order.packOmapiId)),
          order.packOmapiIdPrice,
          1),
        new Item(
          new ProductInfo(order.subPackOmapiId, OmapiIdConstants.PRODUCT_NAME_MAP.get(order.subPackOmapiId)),
          order.subPackOmapiIdPrice,
          1)];
    }

    return [
      new Item(
        new ProductInfo(order.packOmapiId, OmapiIdConstants.PRODUCT_NAME_MAP.get(order.packOmapiId)),
        order.packOmapiIdPrice,
        1)];
  }

  private static mapToTransaction(order: AnalyticsOrder): Transaction {
    return new Transaction(new Price(order.totalPrice()), this.createCartItems(order));
  }

  public getPage(): Page {
    return this.page;
  }

  public getCart(): Cart {
    return this.cart;
  }

  public getAppId(): string {
    return this.appId;
  }

  public getBrand(): string {
    return this.brand;
  }

  public getUser(): User {
    return this.user;
  }

  public getProducts(): Product[] {
    return this.product;
  }

  public getEvents(): Event[] {
    return this.event;
  }

  public getTransaction(): Transaction {
    return this.transaction;
  }

  public getLastEvent(): Event {
    let lastEvent;
    const events: Event[] = this.getEvents();
    if (events !== undefined && events.length > 0) {
      lastEvent = events[events.length - 1];
    }
    return lastEvent;
  }

  public getLastUser(): Event {
    let lastUser;
    if (this.user !== undefined) {
      lastUser = this.user;
    }
    return lastUser;
  }
}
